import { useState, useEffect, useRef } from 'react'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'

interface UseQuoteNameEditingProps {
  quoteId: number | null
  quoteName: string
  defaultName?: string
}

export const useQuoteNameEditing = ({
  quoteId,
  quoteName,
  defaultName = 'Untitled request',
}: UseQuoteNameEditingProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editingName, setEditingName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const { toast } = useToast()
  const { t } = useLocalization()

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = () => {
    setEditingName(quoteName)
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!quoteId) return

    const trimmedName = editingName.trim()
    const finalName = trimmedName || defaultName

    try {
      await updateQuote.mutateAsync({
        id: quoteId,
        name: finalName,
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update quote name:', error)
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: t('Failed to rename request', 'Kunde inte byta namn på förfrågan'),
      })
    }
  }

  const handleCancel = () => {
    setEditingName('')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  return {
    isEditing,
    editingName,
    setEditingName,
    inputRef,
    handleStartEdit,
    handleSave,
    handleCancel,
    handleKeyDown,
  }
}
